import {
  SEARCH_ALGO,
  SESSION_STORAGE_LOCATION_MESSAGE,
  userSelectedConstants,
} from '../constants';
import {
  getEnv,
  getUserSelectedInfo,
  isServerless,
  setSessionStorageIfNotExist,
  setUserSelectedInfo,
  isDisableUserSelectLocationAction,
  isUseUserLocationCode,
  checkCurrentPageIsExcluded,
  getCookieByName,
} from 'helpers';
import { LocationMessageType, TenantEnv } from 'types';
import { BrowserEnv } from 'libs';
import { getQueryParams, NumberParam, StringParam } from 'hooks/useQuery';
import { getDataFromIframe } from '../utils/iframe';
import { inIframe } from '@teko/utils';

// linter replaces "interface" with "type"
// which causes compile error
// so disable lint for next line
// eslint-disable-next-line
interface ClientTenantEnvImpl extends TenantEnv {}
class ClientTenantEnvImpl {
  constructor(config: TenantEnv) {
    // allow users to set terminalId via query parameter
    // use a new storage key to avoid being overwritten by ecom-284 logic
    const isEcom284 =
      getEnv('EPIC_ECOM_284') === 'enabled' && !config.disableEpicEcom284;
    if (isServerless()) {
      const { terminalCode, locationCode, terminalId } = getQueryParams(
        window?.location?.search,
        {
          terminalCode: StringParam,
          locationCode: StringParam,
          terminalId: NumberParam,
        },
      );
      // set this value to localstorage
      if (!!terminalCode) {
        setUserSelectedInfo(
          userSelectedConstants.TERMINAL,
          terminalCode as string,
          config.isMobile,
        );
      }
      if (!!locationCode) {
        setUserSelectedInfo(
          userSelectedConstants.LOCATION,
          locationCode as string,
          config.isMobile,
        );
      }

      if (!!terminalId) {
        // after ecom-284, use the a new storage key
        // as TERMINAL_ID is ignored by the new flow and should be removed
        setUserSelectedInfo(
          isEcom284
            ? userSelectedConstants.OVERRIDE_TERMINAL_ID
            : userSelectedConstants.TERMINAL_ID,
          terminalId.toString(),
          isEcom284 ? false : config.isMobile,
        );
      }

      if (inIframe()) {
        const { clientId: iframeIamClientId, oauthDomain: iframeOauthDomain } =
          getDataFromIframe();

        // only use these 2 values to init authService if Iframe pass both clientId and oathDomain
        if (!!iframeIamClientId && !!iframeOauthDomain) {
          config.iamClientId = iframeIamClientId;
          config.iamOauthDomain = iframeOauthDomain;
        }
      }

      if (!!config.enableSearchAlgoABTesting) {
        // find search algo in local storage
        const localSearchAlgo = getCookieByName(SEARCH_ALGO.COOKIE);
        const envNoDefaultSortAlgos =
          getEnv('SEARCH_ALGO_NO_DEFAULT_SORT_OPTION') || '';
        const noDefaultSortAlgos =
          typeof envNoDefaultSortAlgos === 'string'
            ? envNoDefaultSortAlgos.split(' ')
            : [];
        /**
         * flag support remove default sort option for A/B testing search algo
         * - check for feature flag enableSearchAlgoABTesting in platform config
         * - if page is not excluded from remove sort option in config workload
         * - if cookie has SEARCH_ALGO field
         * - if SEARCH_ALGO field value (after remove single/double quotes) is v3
         */
        const shouldRemoveDefaultSortOption =
          !!config.enableSearchAlgoABTesting &&
          !!window?.location?.pathname &&
          !checkCurrentPageIsExcluded(window?.location?.pathname) &&
          !!localSearchAlgo &&
          noDefaultSortAlgos.includes(
            localSearchAlgo
              .match(new RegExp(`(^| )${SEARCH_ALGO.COOKIE}=([^;]+)`))?.[2]
              ?.replace(SEARCH_ALGO.REPLACE_PATTERN, '') || '',
          );

        if (!!shouldRemoveDefaultSortOption) {
          delete config.defaultSortOptionCode;
        }
      }
    }

    if (isEcom284) {
      // From ecom 284, shop proxy no longer caches responses by terminal or by location,
      // so the terminal and location returned by server are unchanged for every requests.
      // This statement handles re-assigning these configs based on the chosen one of the users.
      // It also sets some variables to storages for showing some necessary popups.
      if (isServerless()) {
        if (!config.locations?.length) {
          // Show the error popup when locations aren't found
          setSessionStorageIfNotExist(
            SESSION_STORAGE_LOCATION_MESSAGE,
            LocationMessageType.Error,
          );
        } else if (
          config.locations.length === 1 &&
          config.locations[0].locationCode === '0'
        ) {
          // With the above condition, don't show any messages.
          setSessionStorageIfNotExist(
            SESSION_STORAGE_LOCATION_MESSAGE,
            LocationMessageType.None,
          );
        } else {
          // Use the location that chose by users and stored in localStorage.
          const locationCodeFromLS = localStorage.getItem(
            userSelectedConstants.LOCATION,
          );

          if (!locationCodeFromLS) {
            // If the location in localStorage is not exist, use the default one returned by the server.
            // Show the hint for this case
            setSessionStorageIfNotExist(
              SESSION_STORAGE_LOCATION_MESSAGE,
              LocationMessageType.Hint,
            );

            // Set the default location to localStorage
            localStorage.setItem(
              userSelectedConstants.LOCATION,
              config.location ?? '',
            );
          } else {
            // If the user has chosen a location that stored in localStorage, verify it in the list returned by server.
            const locationOptionFromConfig = config.locations.find(
              ({ locationCode }) => locationCode === locationCodeFromLS,
            );

            if (!locationOptionFromConfig) {
              // If the chosen of user is not valid, handle it as the case of the location not being chosen
              setSessionStorageIfNotExist(
                SESSION_STORAGE_LOCATION_MESSAGE,
                LocationMessageType.Hint,
              );
              localStorage.setItem(
                userSelectedConstants.LOCATION,
                config.location ?? '',
              );
            } else {
              // If the chosen of user is valid, don't show any messages also.
              // Re-assign the configs by the chosen one of the user.
              setSessionStorageIfNotExist(
                SESSION_STORAGE_LOCATION_MESSAGE,
                LocationMessageType.None,
              );

              config.location = locationOptionFromConfig.locationCode;

              // Ecom 284 do not distinguish terminal between mobile and desktop,
              // so we still need to hard code the mobile one in workload config.
              // This step will get the right terminal based on device types.
              if (!config.isMobile) {
                config.terminalId = locationOptionFromConfig.terminalId;
                config.terminal = locationOptionFromConfig.terminalCode;
              } else {
                const terminalForMobile =
                  config.mobileTerminalIdMapping?.[
                    locationOptionFromConfig.terminalId.toString()
                  ];

                config.terminalId =
                  terminalForMobile?.terminalId ||
                  locationOptionFromConfig.terminalId;
                config.terminal =
                  terminalForMobile?.terminalCode ||
                  locationOptionFromConfig.terminalCode;
              }
            }
          }
        }

        // if there is a overriding terminal id set
        // honor it.
        config.terminalId =
          parseInt(
            getUserSelectedInfo(userSelectedConstants.OVERRIDE_TERMINAL_ID) ??
              '',
          ) || config.terminalId;
      }
    } else {
      // Get terminal from cookie in case tenant has terminal by region (ex: karavan, umbala)
      // This help avoid disk cache from browser when change location
      // More detail: https://jira.teko.vn/browse/UX4BUYER-2566
      if (Array.isArray(config.originalTerminal)) {
        if (
          !isDisableUserSelectLocationAction({ isDmsFlow: config.isDmsFlow })
        ) {
          config.terminal =
            getUserSelectedInfo(
              userSelectedConstants.TERMINAL,
              config.isMobile,
            ) || config.terminal;

          config.terminalId =
            parseInt(
              getUserSelectedInfo(
                userSelectedConstants.TERMINAL_ID,
                config.isMobile,
              ) ?? '',
            ) || config.terminalId;
        }

        if (!isUseUserLocationCode({ isDmsFlow: config.isDmsFlow })) {
          config.location =
            getUserSelectedInfo(
              userSelectedConstants.LOCATION,
              config.isMobile,
            ) || config.location;
        }
      }
    }

    Object.assign(this, config);

    // assign the object to window
    // this a trick to persist the
    // config object for client-side only pages
    window.__tenantConfig = config;
  }

  getProcessEnv(env: string): string {
    return window?.env?.[env as keyof BrowserEnv] as string;
  }
}

export { ClientTenantEnvImpl };
